package com.dignicate.web.ui.screens

import androidx.compose.runtime.*
import com.dignicate.web.domain.ContentBlock
import com.dignicate.web.domain.DocumentContent
import com.dignicate.web.domain.ListItem
import com.dignicate.web.viewmodels.TermsUiState
import com.dignicate.web.viewmodels.TermsViewModel
import org.jetbrains.compose.web.css.AlignItems
import com.dignicate.web.css.BoxSizing
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import com.dignicate.web.css.boxSizing
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.fontFamily
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.overflowX
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.paddingLeft
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.*

@Composable
fun TermsPage(viewModel: TermsViewModel) {
    val state by viewModel.uiState.collectAsState()

    DisposableEffect(viewModel) {
        viewModel.onCreated()
        onDispose { viewModel.onDispose() }
    }

    // Use the layout from PrivacyPolicyPage.kt for consistency
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            minHeight(60.vh)
            padding(40.px)
            boxSizing(BoxSizing.BorderBox)
        }
    }) {
        Div({
            style {
                maxWidth(900.px)
                width(100.percent)
                lineHeight(1.6.toString())
            }
        }) {
            when (state) {
                is TermsUiState.Loading -> {
                    H1 { Text("Terms & Conditions") }
                    P({ style { marginTop(16.px); color(Color("#666")) } }) { Text("Loading...") }
                }
                is TermsUiState.Error -> {
                    H1 { Text("Terms & Conditions") }
                    val msg = (state as TermsUiState.Error).message
                    P({ style { marginTop(16.px); color(Color("#c44")) } }) { Text("Error loading terms: $msg") }
                }
                is TermsUiState.Ready -> {
                    val content = (state as TermsUiState.Ready).content
                    DocumentRenderer(content)
                }
            }
        }
    }
}

@Composable
private fun DocumentRenderer(content: DocumentContent) {
    // content.title is NOT rendered here, because it's already included in the blocks.
    // This avoids rendering the title twice.

    content.blocks.forEach { block ->
        when (block) {
            is ContentBlock.Heading -> {
                when (block.level) {
                    1 -> H1({ style { marginTop(24.px); fontSize(1.75.em) } }) { Text(block.text) }
                    2 -> H2({ style { marginTop(20.px); fontSize(1.5.em) } }) { Text(block.text) }
                    3 -> H3({ style { marginTop(18.px); fontSize(1.25.em) } }) { Text(block.text) }
                    else -> H4({ style { marginTop(16.px); fontSize(1.1.em) } }) { Text(block.text) }
                }
            }
            is ContentBlock.Paragraph -> {
                P({ style { marginTop(12.px) } }) { Text(block.text) }
            }
            is ContentBlock.UnorderedList -> {
                Ul({ style { marginTop(12.px); paddingLeft(18.px) } }) {
                    block.items.forEach { item ->
                        Li {
                            when (item) {
                                is ListItem.Text -> Text(item.text)
                                is ListItem.Link -> A(href = item.url, attrs = {
                                    attr("target", "_blank")
                                    attr("rel", "noopener noreferrer")
                                }) { Text(item.text) }
                            }
                        }
                    }
                }
            }
            is ContentBlock.CodeBlock -> {
                Pre({
                    style {
                        backgroundColor(Color("#f5f5f5"))
                        padding(16.px)
                        borderRadius(4.px)
                        marginTop(12.px)
                        overflowX("auto")
                    }
                }) {
                    Code({ style { fontFamily("monospace") } }) {
                        Text(block.code)
                    }
                }
            }
        }
    }
}
